import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { Button } from 'react-bootstrap';
import { selectRequestSubTypesByName } from '../../../../../redux/actions/lenderRequestActions';
import {
  toggleRestrictionSelection,
  resetRemoveDRWizard,
  setRemoveDRNotes,
  selectCurrentRestrictions,
  selectSelectedRestrictions,
  selectRemoveDRNotes
} from '../../../../../redux/features/lender-request/removeDeliveryRestrictionSlice';

import '../../StatusReview/CreateStatusReviewRequest.css';
import './SelectRestrictionsPage.css';

const SUMMARY_ANALYSIS_MAX_LENGTH = 2000;

const SelectRestrictionsPage = ({
  projectId,
  currentRestrictions,
  selectedRestrictions,
  requestNotes,
  formState,
  toggleRestrictionSelection,
  resetRemoveDRWizard,
  setRemoveDRNotes,
  selectRequestSubTypesByName,
  incrementCounter
}) => {
  const history = useHistory();
  const [showValidation, setShowValidation] = useState(false);
  const [summaryAnalysis, setSummaryAnalysis] = useState(requestNotes || '');

  const isSelected = (restriction) => {
    return selectedRestrictions.some(r => r.restrcnId === restriction.restrcnId);
  };

  const handleNext = () => {
    setShowValidation(true);

    // Validate: at least one restriction selected AND summary/analysis filled
    const trimmedSummary = summaryAnalysis.trim();
    
    if (selectedRestrictions.length === 0 || !trimmedSummary) {
      return;
    }

    // Save notes to Redux
    setRemoveDRNotes(trimmedSummary);
    
    // Check if restrictions have changed
    const currentRestrictionIds = selectedRestrictions.map(r => r.restrcnId).sort().join(',');
    const previousRestrictionIds = formState?.selectedRestrictionIds || '';    
    const restrictionsChanged = currentRestrictionIds !== previousRestrictionIds;
    
    // Pass form state, clearing selectedFiles if restrictions changed
    const newFormState = {
      ...formState,
      selectedRestrictionIds: currentRestrictionIds,
      selectedFiles: restrictionsChanged 
        ? [] 
        : formState?.selectedFiles
    };

    // Build the request subtypes (and their required/optional doc types) for the selected
    // restrictions now, once, rather than reactively on every selection change.
    const selectedShortNames = selectedRestrictions
      .map(r => r.restrictionShortName)
      .filter(Boolean);
    selectRequestSubTypesByName(selectedShortNames);

    incrementCounter(newFormState);
  };

  const handleBack = () => {
    // Save current state before going back
    if (summaryAnalysis.trim()) {
      setRemoveDRNotes(summaryAnalysis.trim());
    }
    history.push(`/project/${projectId}`);
  };

  return (
    <div className="request-details-form">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={10}>
          <h1>Select Restrictions</h1>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 0 }}>
          <h6>Step 1 of 4 - Request Configuration</h6>
        </Grid>
        <Grid item xs={12}>
          <div className="form-section restrictions">
            <Grid
              item
              xs={12}
              className="condition-container"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}
            >
              <div
                className="title-txt"
                style={{ flexDirection: 'row', display: 'flex' }}
              >
                <h3 style={{margin: 0}}>
                  Select Delivery Restrictions to Remove<span style={{ color: 'red' }}>*</span>
                </h3>
              </div>
            </Grid>

            <div className="restrictions-list-container">
              {!currentRestrictions ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Loading...
                </div>
              ) : currentRestrictions.length > 0 ? (
                <div className="restrictions-list">
                  {currentRestrictions.map((restriction) => (
                    <div
                      key={restriction.restrcnId || restriction.restrictionId}
                      className="restriction-item"
                    >
                      <input
                        type="checkbox"
                        className="restriction-checkbox"
                        checked={isSelected(restriction)}
                        onChange={() => toggleRestrictionSelection(restriction)}
                      />
                      <div className="restriction-content">
                        <div className="restriction-name">
                          {restriction.restrictionName || restriction.restrcnName}
                        </div>
                        <div className="restriction-description">
                          {restriction.restrictionDescription || restriction.restrcnDescription}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  No delivery restrictions found for this project.
                </div>
              )}
            </div>

            {showValidation && selectedRestrictions.length === 0 && (
              <div className="validation-error" style={{ marginTop: '10px' }}>
                At least one delivery restriction must be selected to proceed.
              </div>
            )}
          </div>

          <div className="form-section comments">
            <h3>
              Summary & Analysis<span style={{ color: 'red' }}>*</span>
            </h3>
            <p>Provide a summary and analysis for this request:</p>
            <textarea
              value={summaryAnalysis}
              onChange={(e) => setSummaryAnalysis(e.target.value)}
              placeholder="Enter your summary and analysis here..."
              maxLength={SUMMARY_ANALYSIS_MAX_LENGTH}
              className="comments-textarea"
            />
            {showValidation && !summaryAnalysis.trim() && (
              <div className="validation-error">Summary & Analysis is required.</div>
            )}
            <div className={`character-count ${summaryAnalysis.length > SUMMARY_ANALYSIS_MAX_LENGTH ? 'over-limit': ''}`}>
              {summaryAnalysis.length}/{SUMMARY_ANALYSIS_MAX_LENGTH}
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Navigation Buttons */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={10}>
          <Grid container justifyContent="space-between">
            <Button
              type="button"
              className="back-button"
              style={{ width: '150px' }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              type="button"
              className="btn-submit"
              onClick={handleNext}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentRestrictions: selectCurrentRestrictions(state),
  selectedRestrictions: selectSelectedRestrictions(state),
  requestNotes: selectRemoveDRNotes(state)
});

const mapDispatchToProps = {
  toggleRestrictionSelection,
  resetRemoveDRWizard,
  setRemoveDRNotes,
  selectRequestSubTypesByName
};

SelectRestrictionsPage.propTypes = {
  projectId: PropTypes.string.isRequired,
  currentRestrictions: PropTypes.arrayOf(PropTypes.object),
  selectedRestrictions: PropTypes.arrayOf(PropTypes.object),
  requestNotes: PropTypes.string,
  formState: PropTypes.object,
  toggleRestrictionSelection: PropTypes.func.isRequired,
  resetRemoveDRWizard: PropTypes.func.isRequired,
  setRemoveDRNotes: PropTypes.func.isRequired,
  selectRequestSubTypesByName: PropTypes.func.isRequired,
  incrementCounter: PropTypes.func.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectRestrictionsPage);

